import { useEffect, useMemo, useState } from 'react';

import {
  IconCar,
  IconChartHistogram,
  IconMenu2,
  IconReport,
  IconSettings,
  IconSpeakerphone,
  IconUser,
} from '@tabler/icons-react';
import classnames from 'classnames';
import { atom, useAtom } from 'jotai';

import {
  LinkKeepParams,
  useNavigateKeepParams,
} from 'components/KeepParamsRouter/KeepParamsRouter';
import { showReportGenerationAtom } from 'components/ReportsGenerationModal';
import SidebarLink from 'components/SideNavbar/SidebarLink';
import SidebarPopover from 'components/SideNavbar/SidebarPopover';
import UserButton from 'components/SideNavbar/UserButton';
import useFeatureFlag from 'hooks/useFeatureFlag';
import usePerformanceResultsNotification from 'pages/Campaign/PerformanceResults/api/usePerformanceResultsNotification/usePerformanceResultsNotification';
import usePostPerformanceResultsNotification from 'pages/Campaign/PerformanceResults/api/usePostPerformanceResultsNotification/usePostPerformanceResultsNotification';
import PerformanceResultsPopup from 'pages/Campaign/PerformanceResults/PerformanceResultsPopup';
import { getPerformanceResultsNotificationNumber } from 'pages/Campaign/PerformanceResults/utils/getPerformanceResultsNotificationNumber';
import { groupCampaignByChainId } from 'pages/Campaign/PerformanceResults/utils/groupCampaignByChainId';
import Flex from '@lotlinx-aws/sparky-component-library/Sparky/Flex';
import VMLogo from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/Lotlinx_logo.svg';
import { LXBrainV2 } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/LXBrainV2';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import {
  campaignNotificationNumberAtom,
  isInitialLoginCampaignNotification,
} from 'store/performanceResultsAtom';
import { FeatureFlag } from 'types/FeatureFlags';
import { useLocation } from 'react-router';

import styles from './SideNavbar.module.scss';
import { useSelectedDealer, useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import useWindowSize from 'hooks/useWindowSize.tsx';
import Tooltip from '@lotlinx-aws/sparky-component-library/Sparky/Tooltip';
import IconButton from '@lotlinx-aws/sparky-component-library/Sparky/IconButton';
import Popover from '@lotlinx-aws/sparky-component-library/Sparky/Popover';
import AbbrSidebarPopover from 'components/SideNavbar/AbbrSidebarPopover';
import useAccess from 'hooks/useAccess';
import useIdpNotification from 'pages/Shoppers/LeadsRevival/api/useIdpNotification/useIdpNotification';
import { formatIdpNotificationCount } from 'pages/Shoppers/LeadsRevival/utils/revealNotifications';
import { idpNotificationAtom, idpRevealCountAtom, idpRevealSeenDateAtom } from 'store/idpAtom';
import { marketInsightVersionStatusAtom } from 'store/marketInsightVersionStatusAtom';
import getActivePopoverFromPathname from 'components/SideNavbar/utils/getActivePopoverFromPathname';
import { getLotGptBaseUrl } from 'util/lotGptUrl';
import { isVmbiSubscriptionExemptDealer } from 'util/vmbiSubscriptionExemptDealers';

export type PopoeverMenuKeys =
  'more' | 'analytics' | 'inventory' | 'campaigns' | 'leadRevival' | 'dealerManagement';

export const sidebarPopoverAtom = atom<PopoeverMenuKeys | undefined>(undefined);

const hideTextHeight = 860;
export const abbreviationIconHeight = 700;

/**
 * SideNavbar
 */
export default function SideNavbar() {
  const { pathname } = useLocation();
  const activePopoverFromRoute = useMemo(() => getActivePopoverFromPathname(pathname), [pathname]);
  const [popoverMenu, setPopoverMenu] = useAtom(sidebarPopoverAtom);
  const [, setShowReportGeneration] = useAtom(showReportGenerationAtom);
  const { isVmbiSubscribedOnly, isLoading: isAccessLoading } = useAccess();
  const [campaignNotificationNumber, setCampaignNotificationNumber] = useAtom(
    campaignNotificationNumberAtom,
  );
  const [idpNotification, setIdpNotification] = useAtom(idpNotificationAtom);
  const [idpRevealCount, setIdpRevealCount] = useAtom(idpRevealCountAtom);
  const [, setIdpRevealSeenDate] = useAtom(idpRevealSeenDateAtom);
  const [isInitialLoginPerformanceResults, setIsInitialLoginPerformanceResults] = useAtom(
    isInitialLoginCampaignNotification,
  );
  const [marketInsightVersionStatus, setMarketInsightVersionStatus] = useAtom(
    marketInsightVersionStatusAtom,
  );
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { height } = useWindowSize();

  const dealer = useSelectedDealer();
  const [selectedDealerId] = useSelectedDealerId();
  const isExemptVmbiDealer = isVmbiSubscriptionExemptDealer(selectedDealerId);
  const showNonVmbiMenuItems = !isVmbiSubscribedOnly;
  const showReportGeneration = useFeatureFlag(FeatureFlag.ENABLE_REPORT_GENERATION);
  const showLexChat = useFeatureFlag(FeatureFlag.ENABLE_LEX_CHAT);
  const showShoppers = useFeatureFlag(FeatureFlag.ENABLE_IDP);
  const isMarketInsightsV2Enabled = useFeatureFlag(FeatureFlag.ENABLE_MARKET_INSIGHTS_V2);

  const navigate = useNavigateKeepParams();

  const { data: notificationData, isLoading: notificationLoading } =
    usePerformanceResultsNotification();
  const notificationMutation = usePostPerformanceResultsNotification();

  const { data: idpNotificationData } = useIdpNotification();

  useEffect(() => {
    const groupedCampaigns = groupCampaignByChainId(notificationData?.campaignDetails ?? []);
    setCampaignNotificationNumber(
      getPerformanceResultsNotificationNumber(
        groupedCampaigns,
        notificationData?.revealedMetrics ?? {},
      ),
    );
    !notificationLoading &&
      setIsInitialLoginPerformanceResults(
        notificationData?.isInitialLogin === undefined || notificationData?.isInitialLogin,
      );
  }, [notificationData]);

  useEffect(() => {
    const next = idpNotificationData?.reviveIds;
    if (!Array.isArray(next)) {
      setIdpNotification([]);
      return;
    }
    setIdpNotification((prev) =>
      prev.length === next.length && prev.every((id, i) => id === next[i]) ? prev : next,
    );
  }, [idpNotificationData?.reviveIds, selectedDealerId]);

  // Absent when Reveal could not be fetched — show no Reveal count rather than a stale one.
  useEffect(() => {
    setIdpRevealCount(idpNotificationData?.revealCount ?? 0);
  }, [idpNotificationData?.revealCount, selectedDealerId]);

  useEffect(() => {
    setIdpRevealSeenDate(idpNotificationData?.revealSeenDate ?? null);
  }, [idpNotificationData?.revealSeenDate, selectedDealerId]);

  useEffect(() => {
    if (height < abbreviationIconHeight) {
      isPopoverOpen && setPopoverMenu('more');
    } else if (isPopoverOpen) {
      setPopoverMenu(undefined);
      setIsPopoverOpen(false);
    }
  }, [isPopoverOpen, height]);

  // Shoppers nav badge: unseen Revive leads plus new Reveal sessions, capped at 999.
  const idpNotificationBadge = formatIdpNotificationCount(idpNotification.length + idpRevealCount);

  let delayRef: NodeJS.Timeout;

  const handleMouseLeave = () => {
    clearTimeout(delayRef);
    delayRef = setTimeout(() => {
      setPopoverMenu(undefined);
    }, 200);
  };

  const handleMouseEnter = () => {
    clearTimeout(delayRef);
  };

  const handleDismiss = () => {
    !notificationLoading &&
      notificationMutation.mutate(
        { revealedMetrics: notificationData?.revealedMetrics, isInitialLogin: false },
        {
          onSuccess: () => {
            setIsInitialLoginPerformanceResults(false);
          },
          onError: () => {
            console.error('Can not update the performance results notification states');
          },
        },
      );
  };

  const handleClick = (buttonKey: PopoeverMenuKeys) => {
    if (buttonKey === popoverMenu) {
      setPopoverMenu(undefined);
    } else {
      setPopoverMenu(buttonKey);
    }
  };

  return (
    <>
      <div className={styles.sidenavbar}>
        <div
          className={styles.topContainer}
          onMouseLeave={() => height >= abbreviationIconHeight && handleMouseLeave()}
          onMouseEnter={() => height >= abbreviationIconHeight && handleMouseEnter()}
        >
          <LinkKeepParams className={styles.logoWrapper} to={'/analytics/market_insights'}>
            <img
              className={styles.logo}
              src={VMLogo}
              alt='Lotlinx logo'
              data-track='nav_sidebar_logo'
              data-track-section='sidebar'
              data-track-target='navigate'
            />
          </LinkKeepParams>
          {height >= abbreviationIconHeight ? (
            <>
              <SidebarLink
                icon={
                  <IconChartHistogram
                    size={32}
                    color={
                      popoverMenu === 'analytics' || activePopoverFromRoute === 'analytics'
                        ? 'white'
                        : 'var(--icon-action)'
                    }
                  />
                }
                buttonKey='analytics'
                text='Analytics'
                popoverMenu={popoverMenu}
                setPopoverMenu={setPopoverMenu}
                hideText={height < hideTextHeight}
                handleClick={() => handleClick('analytics')}
                trigger={height < hideTextHeight ? 'click' : 'hover'}
                enableTooltip={height < hideTextHeight}
                notificationIndicator={
                  marketInsightVersionStatus === 'unseen' && isMarketInsightsV2Enabled
                }
                dark={activePopoverFromRoute === 'analytics'}
              />
              <SidebarLink
                icon={
                  <IconCar
                    size={32}
                    color={
                      popoverMenu === 'inventory' || activePopoverFromRoute === 'inventory'
                        ? 'white'
                        : 'var(--icon-action)'
                    }
                  />
                }
                buttonKey='inventory'
                text='Inventory'
                popoverMenu={popoverMenu}
                setPopoverMenu={setPopoverMenu}
                hideText={height < hideTextHeight}
                handleClick={() => handleClick('inventory')}
                trigger={height < hideTextHeight ? 'click' : 'hover'}
                enableTooltip={height < hideTextHeight}
                dark={activePopoverFromRoute === 'inventory'}
              />
              {isAccessLoading && !isExemptVmbiDealer ? (
                <>
                  <SidebarLinkSkeleton hideText={height < hideTextHeight} />
                  <SidebarLinkSkeleton hideText={height < hideTextHeight} />
                  <SidebarLinkSkeleton hideText={height < hideTextHeight} />
                </>
              ) : (
                showNonVmbiMenuItems && (
                  <>
                    <SidebarLink
                      icon={
                        <IconSpeakerphone
                          size={32}
                          color={
                            popoverMenu === 'campaigns' || activePopoverFromRoute === 'campaigns'
                              ? 'white'
                              : 'var(--icon-action)'
                          }
                        />
                      }
                      buttonKey='campaigns'
                      popoverMenu={popoverMenu}
                      setPopoverMenu={setPopoverMenu}
                      text='Campaigns'
                      badgeContent={
                        campaignNotificationNumber === 0 ? undefined : campaignNotificationNumber
                      }
                      hideText={height < hideTextHeight}
                      handleClick={() => handleClick('campaigns')}
                      trigger={height < hideTextHeight ? 'click' : 'hover'}
                      enableTooltip={height < hideTextHeight}
                      dark={activePopoverFromRoute === 'campaigns'}
                    />
                    {showShoppers && (
                      <SidebarLink
                        icon={
                          <IconUser
                            size={32}
                            color={
                              popoverMenu === 'leadRevival' ||
                              activePopoverFromRoute === 'leadRevival'
                                ? 'white'
                                : 'var(--icon-action)'
                            }
                          />
                        }
                        buttonKey='leadRevival'
                        popoverMenu={popoverMenu}
                        setPopoverMenu={setPopoverMenu}
                        handleClick={() => navigate('leadRevival')}
                        badgeContent={
                          dealer.dealer?.isMajorAccount ? undefined : idpNotificationBadge
                        }
                        text='Shoppers'
                        hideText={height < hideTextHeight}
                        trigger='click'
                        enableTooltip={height < hideTextHeight}
                        dark={activePopoverFromRoute === 'leadRevival'}
                      />
                    )}
                    <SidebarLink
                      icon={
                        <IconSettings
                          size={32}
                          color={
                            popoverMenu === 'dealerManagement' ||
                            activePopoverFromRoute === 'dealerManagement'
                              ? 'white'
                              : 'var(--icon-action)'
                          }
                        />
                      }
                      text='Dealer'
                      setPopoverMenu={setPopoverMenu}
                      text2='management'
                      popoverMenu={popoverMenu}
                      buttonKey='dealerManagement'
                      hideText={height < hideTextHeight}
                      handleClick={() => handleClick('dealerManagement')}
                      trigger={height < hideTextHeight ? 'click' : 'hover'}
                      enableTooltip={height < hideTextHeight}
                      dark={activePopoverFromRoute === 'dealerManagement'}
                    />
                  </>
                )
              )}
            </>
          ) : (
            <Popover
              isOpen={isPopoverOpen}
              setIsOpen={setIsPopoverOpen}
              popupContent={
                <AbbrSidebarPopover
                  campaignNotificationNumber={campaignNotificationNumber}
                  idpNotificationBadgeCount={
                    !showShoppers || dealer.dealer?.isMajorAccount
                      ? undefined
                      : idpNotificationBadge
                  }
                  setIsPopoverOpen={setIsPopoverOpen}
                />
              }
              side='right'
              align='start'
              verticalOffset={24}
              horizontalOffset={-45}
              showCloseButton={false}
              showCarrat={false}
              contentPadding={false}
              contentClassName={styles.popoverMoreContent}
            >
              <Flex column alignItems='center' rowGap='2px'>
                <IconButton
                  aria-label='more'
                  size='lg'
                  variants={isPopoverOpen ? 'filled' : 'outline'}
                  className={classnames([!isPopoverOpen && styles.menuIconBtn])}
                >
                  <IconMenu2
                    size={32}
                    color={isPopoverOpen ? 'var(--icon-inverse)' : 'var(--icon-action)'}
                  />
                </IconButton>
                <Text type='helper-text-01' color={isPopoverOpen ? 'navy' : 'primary'}>
                  More
                </Text>
              </Flex>
            </Popover>
          )}
        </div>

        <div className={styles.bottomContainer}>
          {showReportGeneration &&
            height >= abbreviationIconHeight &&
            isAccessLoading &&
            !isExemptVmbiDealer && <SidebarLinkSkeleton hideText={height < hideTextHeight} />}
          {showReportGeneration &&
            height >= abbreviationIconHeight &&
            showNonVmbiMenuItems &&
            (!isAccessLoading || isExemptVmbiDealer) && (
              <Tooltip disabled={height >= hideTextHeight} content='Reports' side='right'>
                <Flex column width='100%' alignItems='center' rowGap='2px'>
                  <div
                    aria-label='Open Report Generation'
                    onClick={() => setShowReportGeneration(true)}
                    className={styles.bottomButton}
                    data-track='nav_sidebar_reports'
                    data-track-section='sidebar'
                    data-track-target='open_modal'
                  >
                    <IconReport color='#000' size={32} aria-hidden={true} />
                  </div>
                  {height >= hideTextHeight && (
                    <Text type='helper-text-01' alignment='center' color='navy'>
                      Reports
                    </Text>
                  )}
                </Flex>
              </Tooltip>
            )}
          {showLexChat && (
            <Flex column width='100%' alignItems='center' rowGap='2px'>
              <div
                aria-label='Open Report Generation'
                onClick={() => {
                  const token = localStorage.getItem('turnToken');
                  const dealerId = selectedDealerId;
                  const baseUrl = getLotGptBaseUrl(import.meta.env.VITE_APP_DEPLOY_ENV);
                  window.open(`${baseUrl}/lotgpt?dealerId=${dealerId}&token=${token}`, '_blank');
                }}
                className={styles.bottomButton}
                data-track='nav_sidebar_lotgpt_chat'
                data-track-section='sidebar'
                data-track-target='open_external'
              >
                <LXBrainV2 color='#000' size={32} />
              </div>
              {height >= hideTextHeight && (
                <Text type='helper-text-01' alignment='center' color='navy'>
                  LotGPT Chat
                </Text>
              )}
            </Flex>
          )}
          <UserButton isTooltipDisabled={height >= hideTextHeight} />
        </div>
      </div>
      {height >= abbreviationIconHeight && (
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={classnames([
            styles.sidebarPopoverMenu,
            popoverMenu && popoverMenu !== 'more' && styles.open,
            !popoverMenu && styles.closed,
          ])}
        >
          <div className={styles.popoverContent}>
            <SidebarPopover
              activeKey={popoverMenu}
              closeMenu={() => {
                setPopoverMenu(undefined);
              }}
              campaignNotificationNumber={campaignNotificationNumber}
            />
          </div>
        </div>
      )}

      {notificationData?.mostRecentCampaign?.targetedVins &&
        notificationData.mostRecentCampaign.targetedVins > 0 &&
        isInitialLoginPerformanceResults && (
          <PerformanceResultsPopup
            isInitialLoginPerformanceResults={isInitialLoginPerformanceResults}
            handleDismiss={handleDismiss}
            mostRecentCampaign={notificationData.mostRecentCampaign}
          />
        )}
    </>
  );
}

function SidebarLinkSkeleton({ hideText = false }: { hideText?: boolean }) {
  return (
    <div className={styles.sidebarSkeleton}>
      <div className={styles.skeletonIcon} />
      {!hideText && <div className={styles.skeletonText} />}
    </div>
  );
}
